import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import styled, { css } from 'styled-components';
import {
  createExternalDeviceSharedKey,
  getExternalDevice,
  updateExternalDevice,
} from '~/javascripts/admin/actions/devices';
import { useAppSelector } from '~/javascripts/admin/hooks/useApp';
import { selectUserPrivateMoney } from '~/javascripts/admin/reducers/user-private-money';
import Button from '../../atoms/Buttons';
import CopyToClipboard from '../../atoms/CopyToClipboard';
import Grid from '../../atoms/Grid';
import Headings from '../../atoms/Headings';
import Label from '../../atoms/Label';
import Select from '../../atoms/Select';
import ApiResults from '../../molecules/ApiResults';
import BreadCrumb from '../../molecules/BreadCrumb';

type ExternalDevice = Awaited<ReturnType<typeof getExternalDevice>>;

type DeviceStatus = {
  label: string;
  value: boolean;
};

const PageWrapper = styled.div``;

const SectionWrapper = styled(Grid.Container)`
  ${({ theme: { spacing } }) => css`
    margin: ${spacing['8']} 0;

    ${Grid.Item} {
      margin-left: ${spacing['8']};

      ${Label} {
        margin-bottom: ${spacing['0']};
        align-self: center;
      }
    }
  `}
`;

const SelectOptionWrapper = styled.div`
  ${({ theme: { spacing } }) => css`
    display: flex;
    align-items: center;
    gap: ${spacing['4']};
  `}
`;

const CopyTextWrapper = styled.div`
  ${({ theme: { spacing } }) => css`
    display: flex;
    align-items: center;
    gap: ${spacing['12']};
  `}
`;

const CircleStatusIndicator = styled.div<{ $isActive: boolean }>`
  ${({ theme: { colors, borderRadius }, $isActive }) => {
    const backgroundColor = $isActive ? colors.green['300'] : colors.gray['300'];

    return css`
      width: 16px;
      height: 16px;
      border-radius: ${borderRadius.full};
      display: inline-block;
      background-color: ${backgroundColor};
    `;
  }}
`;

const CRUMBS = [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: '各種設定', href: '', isCurrentPage: true },
  { value: '社外デバイス管理', href: '/settings/device-keys', isCurrentPage: false },
  { value: '社外デバイス詳細', href: '', isCurrentPage: true },
];

const statusOptions: DeviceStatus[] = [
  { label: '有効', value: true },
  { label: '無効', value: false },
];

const formatOptionLabel = (option: DeviceStatus) => {
  return (
    <SelectOptionWrapper>
      <CircleStatusIndicator $isActive={option.value} />
      <div>{option.label}</div>
    </SelectOptionWrapper>
  );
};

const DeviceKeysDetail = ({ match }: RouteComponentProps<{ id?: string }>) => {
  const deviceId = match.params.id;
  const [device, setDevice] = useState<ExternalDevice | null>(null);

  const user = useAppSelector((state) => state.user);
  const { organization: privateMoneyOrg } = useAppSelector(selectUserPrivateMoney);

  const [selectedStatus, setSelectedStatus] = useState<DeviceStatus>(statusOptions[0]);
  const [apiMessages, setApiMessages] = useState<{ text: string; isError?: boolean }[] | undefined>(
    undefined
  );

  const isMember =
    privateMoneyOrg?.code !== 'pocketchange' && privateMoneyOrg?.code !== user.organization?.code;

  const fetchDevice = (preserveStatus = false) => {
    if (deviceId) {
      getExternalDevice(deviceId).then((res) => {
        setDevice(res);

        if (!preserveStatus) {
          const status = statusOptions.find((s) => s.value === !res.is_terminal_invalidated);
          if (status) {
            setSelectedStatus(status);
          }
        }
      });
    }
  };

  const handleStatusChange = (status: DeviceStatus) => {
    setSelectedStatus(status);
  };

  const handleUpdate = () => {
    if (!deviceId) return;

    setApiMessages(undefined);

    updateExternalDevice(deviceId, { is_active: selectedStatus.value })
      .then(() => {
        setApiMessages([
          {
            text: '社外デバイスの更新に成功しました',
            isError: false,
          },
        ]);
        fetchDevice();
      })
      .catch((error) => {
        setApiMessages([
          {
            text: error.message || '社外デバイスの更新に失敗しました',
            isError: true,
          },
        ]);
      });
  };

  const handleRegenerateSharedKey = () => {
    if (!deviceId) return;

    setApiMessages(undefined);

    createExternalDeviceSharedKey(deviceId, { regenerate_shared_key: true })
      .then(() => {
        setApiMessages([
          {
            text: 'SharedKeyの再発行に成功しました',
            isError: false,
          },
        ]);

        fetchDevice(true);
      })
      .catch((error) => {
        setApiMessages([
          {
            text: error.message || 'SharedKeyの再発行に失敗しました',
            isError: true,
          },
        ]);
      });
  };

  useEffect(() => {
    fetchDevice();
  }, [deviceId]);

  return (
    <PageWrapper>
      <BreadCrumb crumbs={CRUMBS} />
      <Headings.h5>社外デバイス詳細</Headings.h5>
      {device && (
        <SectionWrapper>
          <Grid.Item size={2}>
            <Label>状態</Label>
          </Grid.Item>
          <Grid.Item size={10}>
            <div style={{ width: '20%' }}>
              <Select
                fullWidth
                options={statusOptions}
                value={selectedStatus}
                onChange={handleStatusChange}
                formatOptionLabel={formatOptionLabel}
                isDisabled={isMember}
              />
            </div>
          </Grid.Item>
          <Grid.Divider />
          <Grid.Item size={2}>
            <Label>ID</Label>
          </Grid.Item>
          <Grid.Item size={10}>
            <Label>{device.terminal_keys_id}</Label>
          </Grid.Item>
          <Grid.Divider />
          <Grid.Item size={2}>
            <Label>組織名</Label>
          </Grid.Item>
          <Grid.Item size={10}>
            <Label>{device.organization_name}</Label>
          </Grid.Item>
          <Grid.Divider />
          <Grid.Item size={2}>
            <Label>店舗名</Label>
          </Grid.Item>
          <Grid.Item size={10}>
            <Label>{device.shop_name}</Label>
          </Grid.Item>
          <Grid.Divider />
          <Grid.Item size={2}>
            <Label>端末ID</Label>
          </Grid.Item>
          <Grid.Item size={10}>
            <CopyTextWrapper>
              <Label>{device.terminal_id}</Label>
              <CopyToClipboard text={device.terminal_id} tooltipId="copyTerminalId" />
            </CopyTextWrapper>
          </Grid.Item>
          <Grid.Divider />
          <Grid.Item size={2}>
            <Label>SharedKey</Label>
          </Grid.Item>
          <Grid.Item size={10}>
            <CopyTextWrapper>
              <Label>{device.shared_key}</Label>
              <CopyToClipboard text={device.shared_key} tooltipId="copySharedKey" />
              <Button variant="outline" onClick={handleRegenerateSharedKey} disabled={isMember}>
                再発行
              </Button>
            </CopyTextWrapper>
          </Grid.Item>
          <Grid.Divider />
          <Grid.Item size={2}>
            <Label>SharedKey発行日時</Label>
          </Grid.Item>
          <Grid.Item size={10}>
            <Label>
              {device.shared_key_created_at
                ? moment(device.shared_key_created_at).format('YYYY/MM/DD HH:mm:ss')
                : '-'}
            </Label>
          </Grid.Item>
          <Grid.Divider />
        </SectionWrapper>
      )}
      <ApiResults messages={apiMessages} />
      <Button onClick={handleUpdate} disabled={isMember}>
        更新
      </Button>
    </PageWrapper>
  );
};

export default DeviceKeysDetail;
